import { forwardRef, useCallback, useRef, useState } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { AVAILABLE_VARIABLES } from './agent-detail.constants'
import { cn } from '@/lib/utils'

/** Minimal shape the autocomplete needs — lets trigger variables (arbitrary
 * names) be passed alongside the typed AVAILABLE_VARIABLES. */
export type SuggestableVariable = {
  name: string
  description: string
  required: boolean
  example?: string
}

type VariableAutocompleteTextareaProps = React.ComponentProps<
  typeof Textarea
> & {
  onValueChange?: (value: string) => void
  variables?: readonly SuggestableVariable[]
  /** True when the host textarea is text-xs; shrinks popover rows to match. */
  compact?: boolean
}

/**
 * Parse text and cursor position to detect if we're inside {{...}} and get the partial variable name.
 */
function getVariableTrigger(
  text: string,
  cursorPos: number,
): { partial: string; start: number } | null {
  const beforeCursor = text.slice(0, cursorPos)
  const lastOpen = beforeCursor.lastIndexOf('{{')
  if (lastOpen === -1) return null
  const afterOpen = beforeCursor.slice(lastOpen + 2)
  const closeIdx = afterOpen.indexOf('}}')
  if (closeIdx !== -1) return null
  return { partial: afterOpen, start: lastOpen }
}

export const VariableAutocompleteTextarea = forwardRef<
  HTMLTextAreaElement,
  VariableAutocompleteTextareaProps
>(function VariableAutocompleteTextarea(
  {
    value,
    onChange,
    onValueChange,
    onBlur,
    onKeyDown: outerKeyDown,
    className,
    variables = AVAILABLE_VARIABLES,
    compact = false,
    ...props
  },
  ref,
) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const defaultInputRef = useRef<HTMLInputElement>(null)
  const [trigger, setTrigger] = useState<{
    partial: string
    start: number
  } | null>(null)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [defaultForVar, setDefaultForVar] = useState<string | null>(null)
  const [defaultValue, setDefaultValue] = useState('')

  const strValue = typeof value === 'string' ? value : ''

  const filtered = trigger
    ? variables.filter(v =>
        v.name.toLowerCase().includes(trigger.partial.toLowerCase()),
      )
    : []
  const requiredSuggestions = filtered.filter(v => v.required)
  const optionalSuggestions = filtered.filter(v => !v.required)
  const suggestions = [...requiredSuggestions, ...optionalSuggestions]

  const isOpen = trigger !== null && suggestions.length > 0

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e)
      onValueChange?.(e.target.value)
      const pos = e.target.selectionStart ?? 0
      const t = getVariableTrigger(e.target.value, pos)
      setTrigger(t)
      setHighlightedIndex(0)
    },
    [onChange, onValueChange],
  )

  const insertVariable = useCallback(
    (variableName: string, fallback?: string) => {
      if (!textareaRef.current || !trigger) return
      const text = strValue
      const before = text.slice(0, trigger.start)
      const after = text.slice(trigger.start + 2 + trigger.partial.length)
      const replacement = fallback
        ? `{{${variableName} | '${fallback}'}}`
        : `{{${variableName}}}`
      const newValue = before + replacement + after
      const syntheticEvent = {
        target: { value: newValue },
      } as React.ChangeEvent<HTMLTextAreaElement>
      onChange?.(syntheticEvent)
      onValueChange?.(newValue)
      setTrigger(null)
      setDefaultForVar(null)
      setDefaultValue('')
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          const newPos = before.length + replacement.length
          textareaRef.current.focus()
          textareaRef.current.setSelectionRange(newPos, newPos)
        }
      })
    },
    [strValue, trigger, onChange, onValueChange],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!trigger || suggestions.length === 0) {
        outerKeyDown?.(e)
        return
      }

      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        const selected = suggestions[highlightedIndex] ?? suggestions[0]
        if (selected) {
          insertVariable(selected.name)
        }
        return
      }

      if (e.key === 'Escape') {
        e.preventDefault()
        setTrigger(null)
        return
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex(i => (i < suggestions.length - 1 ? i + 1 : 0))
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex(i => (i > 0 ? i - 1 : suggestions.length - 1))
        return
      }
    },
    [trigger, suggestions, highlightedIndex, insertVariable, outerKeyDown],
  )

  const popoverRef = useRef<HTMLDivElement>(null)

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Small delay to allow click on popover items to register
      setTimeout(() => {
        // Don't close if focus moved to the popover (e.g. default input)
        if (popoverRef.current?.contains(document.activeElement)) return
        setTrigger(null)
        setDefaultForVar(null)
        setDefaultValue('')
      }, 200)
      onBlur?.(e)
    },
    [onBlur],
  )

  const renderSection = (
    title: string,
    items: typeof suggestions,
    startIndex: number,
  ) => {
    if (items.length === 0) return null
    return (
      <>
        <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {title}
        </div>
        {items.map((v, j) => {
          const i = startIndex + j

          return (
            <div key={v.name} className="relative">
              <div
                role="option"
                aria-selected={i === highlightedIndex}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 mx-1.5 transition-colors rounded-md cursor-pointer hover:bg-accent',
                  compact ? 'text-xs' : 'text-sm',
                  i === highlightedIndex && 'bg-accent',
                )}
                onMouseEnter={() => setHighlightedIndex(i)}
                onMouseDown={e => {
                  e.preventDefault()
                  insertVariable(v.name)
                }}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    <code className={compact ? 'text-xs' : 'text-sm'}>
                      {'{{' + v.name + '}}'}
                    </code>
                  </div>
                  <div
                    className={cn(
                      'text-muted-foreground truncate',
                      compact ? 'text-[10px]' : 'text-xs',
                    )}
                  >
                    {v.description}
                  </div>
                </div>
                {!v.required && (
                  <button
                    className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded border border-muted-foreground/30 text-muted-foreground hover:text-foreground hover:border-foreground/50"
                    onMouseDown={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      setDefaultForVar(defaultForVar === v.name ? null : v.name)
                      setDefaultValue('')
                      requestAnimationFrame(() =>
                        defaultInputRef.current?.focus(),
                      )
                    }}
                  >
                    default
                  </button>
                )}
              </div>
              {defaultForVar === v.name && (
                <div
                  className="absolute left-full top-0 ml-1 z-50 bg-popover border rounded-md shadow-md p-2 w-64"
                  onMouseDown={e => e.preventDefault()}
                >
                  <div className="text-xs text-muted-foreground mb-1.5">
                    Fallback for <code>{v.name}</code>
                  </div>
                  <input
                    ref={defaultInputRef}
                    type="text"
                    className="w-full text-xs border rounded px-2 py-1.5 outline-none focus:ring-1 focus:ring-primary mb-2"
                    placeholder={v.example || 'Enter default value'}
                    value={defaultValue}
                    onChange={e => setDefaultValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && defaultValue.trim()) {
                        e.preventDefault()
                        insertVariable(v.name, defaultValue.trim())
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault()
                        setDefaultForVar(null)
                        setDefaultValue('')
                      }
                    }}
                  />
                  <div className="flex gap-1.5 justify-end">
                    <button
                      className="text-xs px-2 py-1 rounded text-muted-foreground hover:text-foreground"
                      onMouseDown={e => {
                        e.preventDefault()
                        setDefaultForVar(null)
                        setDefaultValue('')
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50"
                      disabled={!defaultValue.trim()}
                      onMouseDown={e => {
                        e.preventDefault()
                        if (defaultValue.trim()) {
                          insertVariable(v.name, defaultValue.trim())
                        }
                      }}
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </>
    )
  }

  return (
    <Popover open={isOpen}>
      <PopoverAnchor asChild>
        <Textarea
          ref={el => {
            textareaRef.current = el
            if (typeof ref === 'function') ref(el)
            else if (ref) ref.current = el
          }}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className={className}
          {...props}
        />
      </PopoverAnchor>
      <PopoverContent
        ref={popoverRef}
        className="w-72 max-h-54 overflow-auto p-1"
        align="start"
        side="bottom"
        onOpenAutoFocus={e => e.preventDefault()}
        onCloseAutoFocus={e => e.preventDefault()}
      >
        {renderSection('Required', requiredSuggestions, 0)}
        {renderSection(
          'Optional',
          optionalSuggestions,
          requiredSuggestions.length,
        )}
      </PopoverContent>
    </Popover>
  )
})
